'use client';

import { AnalyticsEvent } from '@/shared/lib/analytics/events';
import { useAuthStore } from '@/shared/stores/auth-store';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';
import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Surfaces a toast when an authenticated user lands on any route with
 * ?email= in the URL — the LP forwarder's typed email is going to be
 * ignored, so inform them. Strips ?email= immediately after firing.
 *
 * Reads the search string from `window.location.search` inside the effect
 * rather than calling `useSearchParams()` at render time. The hook would
 * trigger Next.js's bailout-to-CSR for any static prerender pass that
 * traverses this provider (mounted in the root layout), forcing every
 * static page into client-only render and producing dev-mode hydration
 * warnings as the Suspense boundary reconciles. Since the component only
 * needs params for a side-effect, the hook is unnecessary.
 *
 * Gates only on isAuthenticated (default false from create(), set
 * optimistically true on rehydrate when a user exists). Avoids gating on
 * isInitialized — that flag is only set by checkAuth() running from the
 * persist onRehydrateStorage callback, which bails when localStorage is
 * empty, so the gate would hang for fresh sessions.
 */
export function AlreadySignedInToast() {
  const pathname = usePathname();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userEmail = useAuthStore((s) => s.user?.email);

  useEffect(() => {
    if (!isAuthenticated || !userEmail) return;
    // /choose-plan renders a full-state mismatch screen instead of this toast,
    // and cleans ?email= from the URL itself.
    if (pathname === '/choose-plan') return;

    const params = new URLSearchParams(window.location.search);
    if (!params.has('email')) return;

    toast(`You're already signed in as ${userEmail}. Log out to switch accounts.`);

    posthog.capture(AnalyticsEvent.AUTH_ALREADY_SIGNED_IN_TOAST_SHOWN, {
      source_route: pathname,
      session_email_domain: userEmail.split('@')[1] ?? null,
    });

    params.delete('email');
    const query = params.toString();
    window.history.replaceState({}, '', query ? `${pathname}?${query}` : pathname);
  }, [isAuthenticated, userEmail, pathname]);

  return null;
}
